import { jwtDecode } from "jwt-decode";
import AuthService from "@/services/authService";
import api from "@/api";

/**
 * Service de gestion des tokens vétérinaires pour les délégués
 * Permet le basculement transparent vers un JWT vétérinaire lors du calcul de ration
 */
class VeterinaryTokenService {
  static VETERINARY_JWT = null;
  static STORAGE_KEY = "veterinary_token_cache";
  static CONTEXT_KEY = "veterinary_context_active";
  static CACHE_TIMESTAMP_KEY = "veterinary_token_timestamp";
  static CACHE_DURATION = 30 * 60 * 1000; // 30 minutes en millisecondes

  /**
   * Définit le token vétérinaire à utiliser
   * @param {string} token - JWT vétérinaire valide
   */
  static setVeterinaryToken(token) {
    if (!token || typeof token !== "string") {
      console.warn("[VeterinaryTokenService] Token invalide fourni");
      return false;
    }

    try {
      const decoded = jwtDecode(token);
      if (!decoded || !decoded.exp) {
        console.warn("[VeterinaryTokenService] Token JWT malformé");
        return false;
      }
      const currentTime = Date.now() / 1000;
      if (decoded.exp <= currentTime) {
        console.warn("[VeterinaryTokenService] Token JWT expiré");
        return false;
      }

      this.VETERINARY_JWT = token;
      try {
        localStorage.setItem(this.STORAGE_KEY, token);
      } catch (e) {
        console.warn("[VeterinaryTokenService] Impossible de mettre en cache le token");
      }
      return true;
    } catch (error) {
      console.error("[VeterinaryTokenService] Erreur lors de la validation du token:", error);
      return false;
    }
  }

  /**
   * Récupère le token vétérinaire actuel (synchrone)
   * @returns {string|null} Token vétérinaire ou null
   */
  static getVeterinaryToken() {
    try {
      const cachedToken = localStorage.getItem(this.STORAGE_KEY);
      if (cachedToken && this.isVeterinaryTokenValid(cachedToken) && this.isCacheValid()) {
        this.VETERINARY_JWT = cachedToken;
        return cachedToken;
      }
    } catch (e) {
      console.warn("[VeterinaryTokenService] Erreur lecture cache localStorage");
    }
    return null;
  }

  /**
   * Récupère le token vétérinaire avec appel au web service si nécessaire (asynchrone)
   * @returns {Promise<string|null>} Token vétérinaire ou null
   */
  static async getVeterinaryTokenAsync() {
    const memoryToken = this.getVeterinaryToken();
    if (memoryToken) {
      return memoryToken;
    }
    return await this.fetchVeterinaryTokenFromWebService();
  }

  /**
   * Vérifie si le token vétérinaire est valide
   * @param {string} token - Token à vérifier (optionnel)
   * @returns {boolean} True si valide
   */
  static isVeterinaryTokenValid(token = null) {
    const tokenToCheck = token || this.getVeterinaryToken();
    if (!tokenToCheck) {
      return false;
    }

    try {
      const decoded = jwtDecode(tokenToCheck);
      const currentTime = Date.now() / 1000;
      const isValid = decoded.exp > currentTime + 30;
      return isValid;
    } catch (error) {
      console.warn("[VeterinaryTokenService] Token invalide:", error.message);
      return false;
    }
  }

  /**
   * Active le contexte vétérinaire (utilisé dans CalculRationView)
   */
  static activateVeterinaryContext() {
    try {
      sessionStorage.setItem(this.CONTEXT_KEY, "true");
    } catch (e) {
      console.warn("[VeterinaryTokenService] Impossible d'activer le contexte");
    }
  }

  /**
   * Désactive le contexte vétérinaire
   */
  static deactivateVeterinaryContext() {
    try {
      sessionStorage.removeItem(this.CONTEXT_KEY);
    } catch (e) {
      console.warn("[VeterinaryTokenService] Impossible de désactiver le contexte");
    }
  }

  /**
   * Détermine si on doit utiliser le token vétérinaire de manière réactive
   * @returns {boolean} True si délégué dans l'écosystème supplyvet
   */
  static shouldActivateVeterinaryMode() {
    const isDelegate = this.isCurrentUserDelegate();
    const isInSupplyvetEcosystem = this.isInSupplyvetEcosystem();
    return isDelegate && isInSupplyvetEcosystem;
  }

  /**
   * Détecte si on est dans l'écosystème supplyvet (calcul ration, gestion animaux, etc.)
   * @returns {boolean} True si dans l'écosystème supplyvet
   */
  static isInSupplyvetEcosystem() {
    try {
      const currentUrl = window.location.href;
      const currentPath = window.location.pathname;
      const supplyvetIndicators = [
        "/mon-compte?tab=calcul_ration",
        "calcul_ration",
        "/mon-compte",
        "AnimalAddModal",
        "AnimalEditModal",
      ];
      const isInEcosystem = supplyvetIndicators.some(indicator => {
        return currentUrl.includes(indicator) || currentPath.includes(indicator);
      });
      return isInEcosystem;
    } catch (e) {
      console.error("[VeterinaryTokenService] Erreur détection écosystème:", e);
      return false;
    }
  }

  /**
   * Vérifie si l'utilisateur actuel est un délégué
   * @returns {boolean} True si délégué
   */
  static isCurrentUserDelegate() {
    return AuthService.isDelegue();
  }

  /**
   * @param {string} url - URL de la requête
   * @returns {boolean} True si basculement nécessaire
   */
  static shouldUseVeterinaryToken(url) {
    const isDelegate = this.isCurrentUserDelegate();
    const isVeterinaryRoute = this.isVeterinaryRoute(url);
    let inSupplyvetMode = this.shouldActivateVeterinaryMode();
    if (url.includes("/api/v2/shop/animals")) {
      inSupplyvetMode = true;
    }
    const shouldUse = isDelegate && isVeterinaryRoute && inSupplyvetMode;

    return shouldUse;
  }

  /**
   * Vérifie si l'URL correspond à une route nécessitant des permissions vétérinaires
   * @param {string} url - URL à vérifier
   * @returns {boolean} True si route vétérinaire
   */
  static isVeterinaryRoute(url) {
    if (!url || typeof url !== "string") return false;
    const DELEGATE_ONLY_ROUTES = ["/customers/"];
    if (DELEGATE_ONLY_ROUTES.some(route => url.includes(route))) {
      return false;
    }

    const VETERINARY_ROUTES = [
      // Routes animaux
      "/animals",
      "/animals/",
      "/animal-types",
      "/animal-races",
      "/animal-daily-activities",
      "/animal-physiological-stages",
      "/animal-weight-managements",

      // Routes propriétaires
      "/proprietaires",

      // Routes produits
      "/product-variants",
      "/product-variants/",

      // Routes calcul de ration
      "/rations/calculate",
      "/saved-rations",
      "/rations/grouped-by-owners",
      "/ration/proprietaire/",

      // Adresses vétérinaires
      "/addresses",

      // ROUTES COMPLÈTES
      "/api/v2/shop/animals",
      "/api/v2/shop/animals/",
      "/api/v2/shop/owners",
      "/api/v2/shop/proprietaires",
      "/api/v2/shop/animal-types",
      "/api/v2/shop/animal-races",
      "/api/v2/shop/animal-daily-activities",
      "/api/v2/shop/animal-physiological-stages",
      "/api/v2/shop/animal-weight-managements",
      "/api/v2/shop/ration/proprietaire/",
      "/api/v2/shop/products",
      "/api/v2/shop/product-variants",
      "/api/v2/shop/product-variants/",
    ];

    const isVeterinaryRoute = VETERINARY_ROUTES.some(route => url.includes(route));
    return isVeterinaryRoute;
  }

  /**
   * Appelle le web service pour récupérer un JWT vétérinaire
   * @returns {Promise<string|null>} Token vétérinaire ou null
   */
  static async fetchVeterinaryTokenFromWebService() {
    try {
      const response = await api.post("/delegate/demo-vet-token");
      if (response.data && response.data.token) {
        const token = response.data.token;
        if (this.setVeterinaryToken(token)) {
          try {
            localStorage.setItem(this.CACHE_TIMESTAMP_KEY, Date.now().toString());
          } catch (e) {
            console.warn("[VeterinaryTokenService] Impossible de sauvegarder le timestamp");
          }

          return token;
        } else {
          console.warn("[VeterinaryTokenService] Token reçu du web service invalide");
          return null;
        }
      } else {
        console.warn("[VeterinaryTokenService] Réponse web service invalide - pas de token");
        return null;
      }
    } catch (error) {
      console.error("[VeterinaryTokenService] ❌ Erreur appel web service:", error);
      console.error("[VeterinaryTokenService] ❌ Aucun fallback disponible - token statique supprimé");
      return null;
    }
  }

  /**
   * Vérifie si le cache du token est encore valide
   * @returns {boolean} True si le cache est valide
   */
  static isCacheValid() {
    try {
      const timestamp = localStorage.getItem(this.CACHE_TIMESTAMP_KEY);
      if (!timestamp) return false;

      const cacheAge = Date.now() - parseInt(timestamp);
      return cacheAge < this.CACHE_DURATION;
    } catch (e) {
      return false;
    }
  }

  /**
   * Initialise le service pour un délégué (appelé depuis CalculRationView)
   * @returns {Promise<boolean>} True si initialisé avec succès
   */
  static async initializeForDelegate() {
    if (!this.isCurrentUserDelegate()) {
      return false;
    }
    this.activateVeterinaryContext();
    const token = await this.getVeterinaryTokenAsync();

    if (!token) {
      console.warn("[VeterinaryTokenService] ❌ Impossible de récupérer un token vétérinaire valide");
      console.warn("[VeterinaryTokenService] ❌ Vérifiez que le web service /delegate/demo-vet-token fonctionne");
      return false;
    }
    return true;
  }

  /**
   * Nettoie l'initialisation (appelé lors de la sortie de CalculRationView)
   */
  static cleanup() {
    this.deactivateVeterinaryContext();
  }

  /**
   * Retourne des informations de debug sur l'état du service
   * @returns {Object} Informations de debug
   */
  static getDebugInfo() {
    return {
      isDelegate: this.isCurrentUserDelegate(),
      isContextActive: this.isVeterinaryContextActive(),
      hasValidToken: this.isVeterinaryTokenValid(),
      tokenConfigured: this.VETERINARY_JWT !== null,
      currentUser: AuthService.getUser()?.role || "unknown",
      webServiceOnly: true,
    };
  }
}

export default VeterinaryTokenService;
